import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import {
    CalendarMonth,
    CheckCircleOutline,
    ChevronRight,
    ErrorOutline,
    PaymentsOutlined,
    Search,
    Today,
    AutoStories,
    AppRegistration
} from "@mui/icons-material";

import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import ArtworkPaymentScheduleEditDialog from "../components/Payments/ArtworkPaymentScheduleEditDialog";
import DiaryItemModal, { ViewMode } from "../components/DiaryManagement/DiaryItemModal";
import { DiaryItemUpsertDto, DiaryItemDto } from "../models/diaryTypes";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from 'react-router';
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import FileDownloadOutlinedIcon
    from "@mui/icons-material/FileDownloadOutlined";

import { createApiClient, useApiClient } from "../components/useApiClient";
import { useUser } from '../components/UserContext';
import { ClientUser } from "../models/UserProfile";
import { useCurrency } from '../components/CurrencyContext';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
import { PaymentMilestoneStatus } from "../models/Payments";
import type {
    PaymentSummaryDto,
    PaymentCurrencySummaryDto,
    PaymentMonthlySummaryDto,
    PaymentManagementItemDto,
    PaymentManagementResponseDto
} from "../models/PaymentSummary";


type PaymentQuickFilter =
    | "all"
    | "overdue"
    | "duetoday"
    | "next30days"
    | "outstanding"
    | "completed";


// -----------------------------------------------------------------------------
// Formatting
// -----------------------------------------------------------------------------

function formatCurrency(
    amount: number,
    currency?: string,
): string {

    if (!currency) {
        return amount.toLocaleString();
    }

    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    }
    catch {
        return `${currency} ${amount.toLocaleString()}`;
    }
}

function formatDate(value?: string | null): string {
    if (!value) {
        return "No due date";
    }

    return dayjs(value).format("DD MMM YYYY");
}


// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------

export default function PaymentsPage() {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const navigate = useNavigate();

    const { get, postJson, putJson,put, post, delete: del } = useApiClient();
    const { activeClientId, getAccessToken } = useUser();
    const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);

    const [diaryModalOpen, setDairyModalOpen] = useState(false);
    const [editingDiaryItem, setEditingDiaryItem] = useState<DiaryItemDto | null>(null);
    const [modalViewMode, setModalViewMode] = useState<ViewMode>("edit");

    const [conversionRates, setConversionRates] = useState<Record<string, number>>({});
    const { currency } = useCurrency();

    // Edit Payment Schedule State
    const [
        selectedPaymentScheduleId,
        setSelectedPaymentScheduleId,
    ] = useState<number | null>(null);

    const [
        selectedArtworkId,
        setSelectedArtworkId,
    ] = useState<number>(0);

    const [
        editPaymentScheduleOpen,
        setEditPaymentScheduleOpen,
    ] = useState(false);

    // Edit Payment Schedule Handlers
    function handleEditPaymentSchedule(
        payment: PaymentManagementItemDto
    ) {
        setSelectedPaymentScheduleId(payment.paymentScheduleId);
        setSelectedArtworkId(payment.artworkId ?? 0);

        setEditPaymentScheduleOpen(true);
    }

    function handlePaymentUpdate() {
        void loadPayments();
    }

    async function getDiaryItemById(id: number, includeChildren: boolean) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in getDiaryItemById');

            const qs = includeChildren ? "?includeChildren=true" : "";

            const res = await get<DiaryItemDto>(`diary/${id}${qs}`, accessToken,);
            return res;
        }
        catch (e) {
            throw new Error('Error in getDiaryItemById');
        }
    }
    async function refreshDiaryItem(id: number) {
        const refreshed = await getDiaryItemById(id, true);
        setEditingDiaryItem(refreshed);
        return refreshed;
    }
    async function regenerateSeries(id: number, fromUtc?: string, toUtc?: string) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary/${id}/regenerate`, accessToken, JSON.stringify({ fromUtc, toUtc }))

            return (res);
        }
        catch (e) {
            throw new Error('Error in regenerateSeries');
        }
    }
    async function createChildDiaryItem(parentId: number, dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary/${parentId}/children`, accessToken, JSON.stringify(dto))

            return (res);
        }
        catch (e) {
            throw new Error('Error in regenerateSeries');
        }
    }
    async function createDiaryItem(dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary`, accessToken, JSON.stringify(dto))

            return (res) as { id: number };
        }
        catch (e) {
            console.error('Error in createDiaryItem', e);
            throw new Error('Error in createDiaryItem');
        }
    }
    async function updateDiaryItem(id: number, dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in updateDiaryItem');

            const res = await putJson(`diary/${id}`, accessToken, JSON.stringify(dto));
        }
        catch (e) {
            console.error('Error in updateDiaryItem', e);
            throw new Error('Error in updateDiaryItem');
        }
    }
    async function onSaveDiary(dto: DiaryItemUpsertDto, id?: number) {
        if (!id) {
            await createDiaryItem(dto);
        } else {
            await updateDiaryItem(id, dto);
        }
        setDairyModalOpen(false);
        setEditingDiaryItem(null);
    }
    async function handleCloseDiary() {
        setDairyModalOpen(false);
        setEditingDiaryItem(null);
    }

    const [data, setData] =
        useState<PaymentManagementResponseDto | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [quickFilter, setQuickFilter] =
        useState<PaymentQuickFilter>("outstanding");

    const [viewCurrency, setCurrency] =
        useState<string>("");

    const [search, setSearch] =
        useState("");

    const [searchText, setSearchText] =
        useState("");

    const getExchangeRates = async () => {
        try {
            const accessToken = await getAccessToken();

            if (accessToken && currency) {
                const response = await get<Record<string, number>>(`exchangerates/${currency}`, accessToken);
                setConversionRates(response);
            }
            else {
                throw new Error("Could not get exchange rates!");
            }
        }
        catch (err: any) {
            throw new Error(
                `Unable to load exchange rates. ${err}`,
            );
        }
    };

    useEffect(() => {
        getExchangeRates();
    }, [currency]);

    function convertCurrency(
        value: number,
        fromCurrency: string
    ): number {

        if (!value || fromCurrency === currency) {
            return value;
        }

        const fromRate = conversionRates[fromCurrency];
        const toRate = conversionRates[currency];

        if (!fromRate || !toRate) {
            return value;
        }

        return (value / fromRate) * toRate;
    }

    async function getPayments(
        clientId: number,
        status?: PaymentQuickFilter,
        currency?: string,
        search?: string,
    ): Promise<PaymentManagementResponseDto> {

        try {
            const params = new URLSearchParams();


            params.set("clientId", clientId.toString());

            if (status && status !== "all") {
                params.set("status", status);
            }

            if (currency) {
                params.set("currency", currency);
            }

            if (search?.trim()) {
                params.set("search", search.trim());
            }

            const accessToken = await getAccessToken();

            if (accessToken) {
                const response = await get<PaymentManagementResponseDto>(`/payments?${params.toString()}`, accessToken);
                return response;
            }
            else {
                throw new Error("Could not get token - user not authenticated!");
            }
        } catch (err: any) {

            throw new Error(
                `Unable to load payments. ${err}`,
            );
        }
    }
    const loadPayments = useCallback(async () => {

        if (!activeClientId) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await getPayments(
                activeClientId,
                quickFilter,
                viewCurrency || undefined,
                search || undefined,
            );

            setData(response);
        }
        catch (err) {
            console.error(err);

            setError(
                err instanceof Error
                    ? err.message
                    : "Unable to load payments.",
            );
        }
        finally {
            setLoading(false);
        }

    }, [
        activeClientId,
        quickFilter,
        viewCurrency,
        search,
    ]);

    useEffect(() => {
        void loadPayments();
    }, [loadPayments]);

    const convertedKpis = useMemo(() => {

        const totals = data?.currencyTotals ?? [];

        return totals.reduce(
            (result, item) => {

                result.outstanding += convertCurrency(
                    item.outstandingAmount,
                    item.currency
                );

                result.overdue += convertCurrency(
                    item.overdueAmount,
                    item.currency
                );

                result.dueNext30Days += convertCurrency(
                    item.dueNext30DaysAmount,
                    item.currency
                );

                result.completedYtd += convertCurrency(
                    item.completedYtdAmount,
                    item.currency
                );

                return result;
            },
            {
                outstanding: 0,
                overdue: 0,
                dueNext30Days: 0,
                completedYtd: 0,
            }
        );

    }, [
        data?.currencyTotals,
        currency,
        conversionRates,
    ]);

    // Reset filters when changing client
    useEffect(() => {
        setQuickFilter("outstanding");
        setCurrency("");
        setSearch("");
        setSearchText("");
    }, [activeClientId]);

    const currencies = useMemo(() => {

        if (!data) {
            return [];
        }

        return data.currencyTotals
            .map(x => x.currency)
            .filter(Boolean)
            .sort();

    }, [data]);

    const chartData = useMemo(() => {

        if (!data) {
            return [];
        }

        const grouped = new Map<string, {
            month: string;
            sortDate: number;
            amount: number;
        }>();

        for (const item of data.monthlyCommitments) {

            const date = dayjs()
                .year(item.year)
                .month(item.month - 1)
                .startOf("month");

            const key = `${item.year}-${item.month}`;

            const convertedAmount = convertCurrency(
                item.amount,
                item.currency
            );

            const existing = grouped.get(key);

            if (existing) {
                existing.amount += convertedAmount;
            }
            else {
                grouped.set(key, {
                    month: date.format("MMM YY"),
                    sortDate: date.valueOf(),
                    amount: convertedAmount,
                });
            }
        }

        return Array.from(grouped.values())
            .sort((a, b) =>
                a.sortDate - b.sortDate
            );

    }, [
        data,
        currency,
        conversionRates,
    ]);

    const handleSearch = () => {
        setSearch(searchText.trim());
    };

    const handleSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    const onOpenArtwork = (artworkId: number) => {
        navigate(`/artwork/${artworkId}`);
    }

    const handleOpenPayment = (
        payment: PaymentManagementItemDto
    ) => {
        refreshDiaryItem(payment.parentDiaryItemId);
        setDairyModalOpen(true);
    };

    // Export to Excel
    function getPaymentStatusLabel(
        payment: PaymentManagementItemDto
    ): string {

        if (
            payment.status ===
            PaymentMilestoneStatus.Cancelled
        ) {
            return "Cancelled";
        }

        if (
            payment.status ===
            PaymentMilestoneStatus.Paid
        ) {
            return "Paid";
        }

        if (payment.isOverdue) {
            return "Overdue";
        }

        if (payment.isDueToday) {
            return "Due Today";
        }

        if (
            payment.status ===
            PaymentMilestoneStatus.PartPaid
        ) {
            return "Part Paid";
        }

        if (
            payment.daysUntilDue != null &&
            payment.daysUntilDue >= 0 &&
            payment.daysUntilDue <= 7
        ) {
            return "Due Soon";
        }

        return "Planned";
    }

    const handleExportExcel = () => {

        if (!data?.payments?.length) {
            return;
        }

        const exportRows = data.payments.map(payment => ({
            "Artwork": payment.artworkTitle ?? "",
            "Artist": payment.artistName ?? "",
            "Milestone": payment.label
                ? `[${payment.sequenceNumber}] ${payment.label}` 
                : `Payment ${payment.sequenceNumber}`,

            "Due Date": payment.dueUtc
                ? dayjs(payment.dueUtc).format("DD/MM/YYYY")
                : "",

            "Currency": payment.currency,

            "Amount": payment.amount,

            "Amount Paid": payment.amountPaid,

            "Outstanding":
                payment.outstandingAmount,

            "Status":
                getPaymentStatusLabel(payment),

            "Paid Date": payment.paidUtc
                ? dayjs(payment.paidUtc).format("DD/MM/YYYY")
                : "",

            "Days Until Due": payment.paidUtc
               ? ""
               : payment.daysUntilDue ?? "",
        }));

        const worksheet =
            XLSX.utils.json_to_sheet(exportRows);

        worksheet["!cols"] = [
            { wch: 32 }, // Artwork
            { wch: 24 }, // Artist
            { wch: 24 }, // Milestone
            { wch: 14 }, // Due date
            { wch: 10 }, // Currency
            { wch: 16 }, // Amount
            { wch: 16 }, // Amount paid
            { wch: 16 }, // Outstanding
            { wch: 14 }, // Status
            { wch: 14 }, // Paid date
            { wch: 14 }, // Days until due
        ];

        const workbook =
            XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Payments"
        );

        const fileName =
            `Payments_${dayjs().format("YYYY-MM-DD")}.xlsx`;

        XLSX.writeFile(
            workbook,
            fileName
        );
    };

    if (loading && !data) {
        return (
            <Box
                sx={{
                    minHeight: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error && !data) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography
                    color="error"
                    gutterBottom
                >
                    {error}
                </Typography>

                <Button
                    variant="outlined"
                    onClick={() => void loadPayments()}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    if (!data) {
        return null;
    }

    return (
        <>
            <Stack spacing={3}>

            {/* ------------------------------------------------------------- */}
            {/* Header */}
            {/* ------------------------------------------------------------- */}

            <Stack
                direction={{
                    xs: "column",
                    md: "row",
                }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{
                    xs: "stretch",
                    md: "center",
                }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        fontWeight={600}
                    >
                        Payments
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Collection-wide payment commitments,
                        milestones and outstanding balances.
                    </Typography>
                </Box>

                <Stack
                    direction={{
                        xs: "column",
                        sm: "row",
                    }}
                    spacing={1}
                >
                    <TextField
                        size="small"
                        placeholder="Search payments..."
                        value={searchText}
                        onChange={e =>
                            setSearchText(e.target.value)
                        }
                        onKeyDown={handleSearchKeyDown}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    <Button
                        variant="outlined"
                        onClick={handleSearch}
                    >
                        Search
                    </Button>
                </Stack>
            </Stack>


            {/* ------------------------------------------------------------- */}
            {/* KPI cards */}
            {/* ------------------------------------------------------------- */}

            <Grid container spacing={2}>

                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <SummaryCard
                        title="Outstanding"
                        baseCurrencyAmount={convertedKpis.outstanding}
                        currency={currency}
                        count={data.summary.outstandingCount}
                        icon={<PaymentsOutlined />}
                        amounts={data.currencyTotals.map(x => ({
                            currency: x.currency,
                            amount: x.outstandingAmount,
                        }))}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <SummaryCard
                        title="Overdue"
                        baseCurrencyAmount={convertedKpis.overdue}
                        currency={currency}
                        count={data.summary.overdueCount}
                        icon={<ErrorOutline />}
                        amounts={data.currencyTotals.map(x => ({
                            currency: x.currency,
                            amount: x.overdueAmount,
                        }))}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <SummaryCard
                        title="Due in 30 Days"
                        baseCurrencyAmount={convertedKpis.dueNext30Days}
                        currency={currency}
                        count={data.summary.dueNext30DaysCount}
                        icon={<CalendarMonth />}
                        amounts={data.currencyTotals.map(x => ({
                            currency: x.currency,
                            amount: x.dueNext30DaysAmount,
                        }))}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <SummaryCard
                        title="Paid YTD"
                        baseCurrencyAmount={convertedKpis.completedYtd}
                        currency={currency}
                        count={data.summary.completedYtdCount}
                        icon={<CheckCircleOutline />}
                        amounts={data.currencyTotals.map(x => ({
                            currency: x.currency,
                            amount: x.completedYtdAmount,
                        }))}
                    />
                </Grid>

            </Grid>


            {/* ------------------------------------------------------------- */}
            {/* Cash commitments */}
            {/* ------------------------------------------------------------- */}

            <Card>
                <CardContent>

                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{
                            xs: "stretch",
                            sm: "center",
                        }}
                        mb={2}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={600}
                            >
                                Upcoming Commitments
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Outstanding payment commitments
                                over the next six months.
                            </Typography>
                        </Box>
                    </Stack>

                    {chartData.length === 0 ? (
                        <EmptyChart />
                    ) : (
                        <Box sx={{ height: 280 }}>
                            <ResponsiveContainer
                                width="100%"
                                height="100%"
                            >
                                <BarChart data={chartData}>
                                    <CartesianGrid
                                        vertical={false}
                                    />

                                    <XAxis
                                        dataKey="month"
                                        tickLine={false}
                                    />

                                    <YAxis
                                        tickFormatter={value =>
                                            compactCurrency(
                                                Number(value),
                                                currency,
                                            )
                                        }
                                    />

                                    <RechartsTooltip
                                        formatter={value => [
                                            formatCurrency(
                                                Number(value),
                                                currency,
                                            ),
                                            "Outstanding",
                                        ]}
                                    />

                                    <Bar
                                        dataKey="amount"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    )}

                </CardContent>
            </Card>


            {/* ------------------------------------------------------------- */}
            {/* Filters */}
            {/* ------------------------------------------------------------- */}

            <Card>
                <CardContent
                    sx={{
                        "&:last-child": {
                            pb: 2,
                        },
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        spacing={2}
                        justifyContent="space-between"
                    >

                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            <QuickFilterChip
                                label="All"
                                selected={quickFilter === "all"}
                                onClick={() =>
                                    setQuickFilter("all")
                                }
                            />

                            <QuickFilterChip
                                label={`Overdue ${data.summary.overdueCount}`}
                                selected={
                                    quickFilter === "overdue"
                                }
                                onClick={() =>
                                    setQuickFilter("overdue")
                                }
                            />

                            <QuickFilterChip
                                label={`Due Today ${data.summary.dueTodayCount}`}
                                selected={
                                    quickFilter === "duetoday"
                                }
                                onClick={() =>
                                    setQuickFilter("duetoday")
                                }
                            />

                            <QuickFilterChip
                                label={`Next 30 Days ${data.summary.dueNext30DaysCount}`}
                                selected={
                                    quickFilter === "next30days"
                                }
                                onClick={() =>
                                    setQuickFilter("next30days")
                                }
                            />

                            <QuickFilterChip
                                label={`Outstanding ${data.summary.outstandingCount}`}
                                selected={
                                    quickFilter === "outstanding"
                                }
                                onClick={() =>
                                    setQuickFilter("outstanding")
                                }
                            />

                            <QuickFilterChip
                                label={`Paid ${data.summary.completedYtdCount}`}
                                selected={
                                    quickFilter === "completed"
                                }
                                onClick={() =>
                                    setQuickFilter("completed")
                                }
                            />
                        </Stack>

                        <FormControl
                            size="small"
                            sx={{
                                minWidth: {
                                    xs: "100%",
                                    md: 140,
                                },
                            }}
                        >
                            <Select
                                    value={viewCurrency}
                                displayEmpty
                                onChange={e =>
                                    setCurrency(e.target.value)
                                }
                            >
                                <MenuItem value="">
                                    All currencies
                                </MenuItem>

                                {currencies.map(code => (
                                    <MenuItem
                                        key={code}
                                        value={code}
                                    >
                                        {code}
                                    </MenuItem>
                                ))}
                            </Select>
                            </FormControl>

                            <Button
                                variant="outlined"
                                startIcon={
                                    <FileDownloadOutlinedIcon />
                                }
                                onClick={handleExportExcel}
                                disabled={!data.payments.length}
                            >
                                Export Excel
                            </Button>

                    </Stack>
                </CardContent>
            </Card>


            {/* ------------------------------------------------------------- */}
            {/* Payments */}
            {/* ------------------------------------------------------------- */}

            {isMobile ? (
                <MobilePaymentsList
                    payments={data.payments}
                    onOpenArtwork={onOpenArtwork}
                    onOpenPayment={handleOpenPayment}
                />
            ) : (
                <PaymentsTable
                    payments={data.payments}
                    onOpenArtwork={onOpenArtwork}
                    onOpenPayment={handleOpenPayment}
                    onEditPaymentSchedule={handleEditPaymentSchedule}
                />
            )}

            {loading && (
                <LinearProgress />
            )}

        </Stack>

            {editingDiaryItem &&
                <DiaryItemModal
                    open={diaryModalOpen}
                    item={editingDiaryItem}
                    viewMode={modalViewMode}
                    clientUsers={clientUsers}
                    onClose={handleCloseDiary}
                    onSave={onSaveDiary}
                    refreshParent={refreshDiaryItem}
                    regenerateSeries={regenerateSeries}
                    updateDiaryItem={updateDiaryItem}
                    createChildDiaryItem={createChildDiaryItem}
                />
            }

            {selectedPaymentScheduleId !== null && (
                <ArtworkPaymentScheduleEditDialog
                    key={selectedPaymentScheduleId}
                    open={editPaymentScheduleOpen}
                    paymentScheduleId={selectedPaymentScheduleId}
                    artworkId={selectedArtworkId!}
                    onClose={() => {
                        setEditPaymentScheduleOpen(
                            false
                        );

                        setSelectedPaymentScheduleId(
                            null
                        );
                    }}
                    onUpdated={() => {
                        handlePaymentUpdate();
                    }}
                />
            )}
        </>
    );
}


// -----------------------------------------------------------------------------
// Summary card
// -----------------------------------------------------------------------------

interface SummaryCardProps {
    title: string;
    baseCurrencyAmount: number;
    currency: string;
    count: number;
    icon: React.ReactNode;

    amounts: {
        currency: string;
        amount: number;
    }[];
}

function SummaryCard({
    title,
    baseCurrencyAmount,
    currency,
    count,
    icon,
    amounts,
}: SummaryCardProps) {

    const nonZeroAmounts = amounts.filter(
        x => x.amount !== 0,
    );

    return (
        <Card sx={{ height: "100%" }}>
            <CardContent>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {title}
                        </Typography>

                        <Typography
                            variant="h4"
                            fontWeight={600}
                            sx={{ mt: 0.5 }}
                        >
                            {formatCurrency(
                                baseCurrencyAmount,
                                currency
                            )}
                        </Typography>

                        <Typography
                            variant="h4"
                            fontWeight={600}
                            sx={{ mt: 0.5 }}
                        >
                            {count}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {count === 1
                                ? "milestone"
                                : "milestones"}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            bgcolor: "action.hover",
                            color: "text.secondary",
                        }}
                    >
                        {icon}
                    </Box>
                </Stack>

                <Stack spacing={0.5} mt={2}>
                    {nonZeroAmounts.length === 0 ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            No value
                        </Typography>
                    ) : (
                        nonZeroAmounts.map(x => (
                            <Typography
                                key={x.currency}
                                variant="body2"
                                fontWeight={500}
                            >
                                {formatCurrency(
                                    x.amount,
                                    x.currency,
                                )}
                            </Typography>
                        ))
                    )}
                </Stack>

            </CardContent>
        </Card>
    );
}


// -----------------------------------------------------------------------------
// Desktop table
// -----------------------------------------------------------------------------

interface PaymentsListProps {
    payments: PaymentManagementItemDto[];
    onOpenArtwork?: (artworkId: number) => void;
    onOpenPayment?: (payment: PaymentManagementItemDto) => void;
    onEditPaymentSchedule?: (payment: PaymentManagementItemDto) => void;
}

function PaymentsTable({
    payments,
    onOpenArtwork,
    onOpenPayment,
    onEditPaymentSchedule
}: PaymentsListProps) {

    return (
        <Card>
            <TableContainer>
                <Table>

                    <TableHead>
                        <TableRow>
                            <TableCell>
                                Artwork
                            </TableCell>

                            <TableCell>
                                Payment
                            </TableCell>

                            <TableCell>
                                Due
                            </TableCell>

                            <TableCell align="right">
                                Amount
                            </TableCell>

                            <TableCell align="right">
                                Paid
                            </TableCell>

                            <TableCell align="right">
                                Outstanding
                            </TableCell>

                            <TableCell>
                                Status
                            </TableCell>

                            <TableCell width={50} />
                        </TableRow>
                    </TableHead>

                    <TableBody>

                        {payments.map(payment => (
                            <TableRow
                                key={payment.milestoneId}
                                hover
                            >
                                <TableCell>
                                    <ArtworkCell
                                        payment={payment}
                                        onOpenArtwork={
                                            onOpenArtwork
                                        }
                                    />
                                </TableCell>

                                <TableCell>
                                    <PaymentLabel
                                        payment={payment}
                                    />
                                </TableCell>

                                <TableCell>
                                    <DueDate
                                        payment={payment}
                                    />
                                </TableCell>

                                <TableCell align="right">
                                    {formatCurrency(
                                        payment.amount,
                                        payment.currency,
                                    )}
                                </TableCell>

                                <TableCell align="right">
                                    <PaidValue
                                        payment={payment}
                                    />
                                </TableCell>

                                <TableCell align="right">
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {formatCurrency(
                                            payment.outstandingAmount,
                                            payment.currency,
                                        )}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <PaymentStatusChip
                                        payment={payment}
                                    />
                                </TableCell>

                                <TableCell>
                                    <Tooltip title="View payment">
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                onOpenPayment?.(
                                                    payment,
                                                )
                                            }
                                        >
                                            <AutoStories />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Payment">
                                        <IconButton edge="end" onClick={() =>
                                            onEditPaymentSchedule?.(
                                                payment
                                            )
                                        }>
                                            <AppRegistration />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>

                            </TableRow>
                        ))}

                        {payments.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    align="center"
                                    sx={{ py: 6 }}
                                >
                                    <Typography
                                        color="text.secondary"
                                    >
                                        No payments match the
                                        selected filters.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
}


// -----------------------------------------------------------------------------
// Mobile
// -----------------------------------------------------------------------------

function MobilePaymentsList({
    payments,
    onOpenArtwork,
    onOpenPayment,
}: PaymentsListProps) {

    if (payments.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography
                        color="text.secondary"
                        align="center"
                    >
                        No payments match the selected filters.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Stack spacing={1.5}>
            {payments.map(payment => (

                <Card key={payment.milestoneId}>
                    <CardContent>

                        <Stack spacing={2}>

                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                            >
                                <ArtworkCell
                                    payment={payment}
                                    onOpenArtwork={
                                        onOpenArtwork
                                    }
                                />

                                <PaymentStatusChip
                                    payment={payment}
                                />
                            </Stack>

                            <Box>
                                <PaymentLabel
                                    payment={payment}
                                />

                                <Box mt={1}>
                                    <DueDate
                                        payment={payment}
                                    />
                                </Box>
                            </Box>

                            <Grid container spacing={1}>

                                <Grid size={4}>
                                    <MobileValue
                                        label="Amount"
                                        value={formatCurrency(
                                            payment.amount,
                                            payment.currency,
                                        )}
                                    />
                                </Grid>

                                <Grid size={4}>
                                    <MobileValue
                                        label="Paid"
                                        value={formatCurrency(
                                            payment.amountPaid,
                                            payment.currency,
                                        )}
                                    />
                                </Grid>

                                <Grid size={4}>
                                    <MobileValue
                                        label="Outstanding"
                                        value={formatCurrency(
                                            payment.outstandingAmount,
                                            payment.currency,
                                        )}
                                        strong
                                    />
                                </Grid>

                            </Grid>

                            {payment.status ===
                                PaymentMilestoneStatus.PartPaid && (
                                    <PaymentProgress
                                        payment={payment}
                                    />
                                )}

                            <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ChevronRight />}
                                onClick={() =>
                                    onOpenPayment?.(
                                        payment,
                                    )
                                }
                            >
                                View payment
                            </Button>

                        </Stack>

                    </CardContent>
                </Card>
            ))}
        </Stack>
    );
}


// -----------------------------------------------------------------------------
// Artwork cell
// -----------------------------------------------------------------------------

function ArtworkCell({
    payment,
    onOpenArtwork,
}: {
    payment: PaymentManagementItemDto;
    onOpenArtwork?: (artworkId: number) => void;
}) {

    const clickable =
        payment.artworkId != null &&
        onOpenArtwork != null;

    return (
        <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
                cursor: clickable
                    ? "pointer"
                    : "default",
                minWidth: 180,
            }}
            onClick={() => {
                if (
                    payment.artworkId != null &&
                    onOpenArtwork
                ) {
                    onOpenArtwork(payment.artworkId);
                }
            }}
        >
            <Avatar
                variant="rounded"
                src={
                    payment.primaryImageUrl ??
                    undefined
                }
                sx={{
                    width: 48,
                    height: 48,
                    bgcolor: "action.hover",
                }}
            />

            <Box sx={{ minWidth: 0 }}>

                <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    sx={{ maxWidth: 220 }}
                >
                    {payment.artworkTitle ??
                        "Artwork"}
                </Typography>

                {payment.artistName && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        display="block"
                        sx={{ maxWidth: 220 }}
                    >
                        {payment.artistName}
                    </Typography>
                )}

            </Box>
        </Stack>
    );
}


// -----------------------------------------------------------------------------
// Payment label
// -----------------------------------------------------------------------------

function PaymentLabel({
    payment,
}: {
    payment: PaymentManagementItemDto;
}) {

    return (
        <Box>
            <Typography
                variant="body2"
                fontWeight={500}
            >
                {payment.label ??
                    `Payment ${payment.sequenceNumber}`}
            </Typography>

            <Typography
                variant="caption"
                color="text.secondary"
            >
                Milestone {payment.sequenceNumber}
            </Typography>
        </Box>
    );
}


// -----------------------------------------------------------------------------
// Due date
// -----------------------------------------------------------------------------

function DueDate({
    payment,
}: {
    payment: PaymentManagementItemDto;
}) {

    return (
        <Box>
            <Typography
                variant="body2"
                color={
                    payment.isOverdue
                        ? "error.main"
                        : undefined
                }
                fontWeight={
                    payment.isOverdue
                        ? 600
                        : undefined
                }
            >
                {formatDate(payment.dueUtc)}
            </Typography>

            {payment.isOverdue &&
                payment.daysUntilDue != null && (
                    <Typography
                        variant="caption"
                        color="error"
                    >
                        {Math.abs(
                            payment.daysUntilDue,
                        )}{" "}
                        days overdue
                    </Typography>
                )}

            {payment.isDueToday && (
                <Typography
                    variant="caption"
                    color="warning.main"
                >
                    Due today
                </Typography>
            )}

        </Box>
    );
}


// -----------------------------------------------------------------------------
// Paid amount / progress
// -----------------------------------------------------------------------------

function PaidValue({
    payment,
}: {
    payment: PaymentManagementItemDto;
}) {

    return (
        <Box>
            <Typography variant="body2">
                {formatCurrency(
                    payment.amountPaid,
                    payment.currency,
                )}
            </Typography>

            {payment.status ===
                PaymentMilestoneStatus.PartPaid && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        {getPaidPercentage(payment)}%
                    </Typography>
                )}
        </Box>
    );
}

function PaymentProgress({
    payment,
}: {
    payment: PaymentManagementItemDto;
}) {

    const percentage =
        getPaidPercentage(payment);

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                mb={0.5}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                >
                    Payment progress
                </Typography>

                <Typography
                    variant="caption"
                    fontWeight={500}
                >
                    {percentage}%
                </Typography>
            </Stack>

            <LinearProgress
                variant="determinate"
                value={percentage}
            />
        </Box>
    );
}

function getPaidPercentage(
    payment: PaymentManagementItemDto,
): number {

    if (payment.amount <= 0) {
        return 0;
    }

    return Math.min(
        100,
        Math.max(
            0,
            Math.round(
                (payment.amountPaid /
                    payment.amount) *
                100,
            ),
        ),
    );
}

// -----------------------------------------------------------------------------
// Status
// -----------------------------------------------------------------------------

function PaymentStatusChip({
    payment,
}: {
    payment: PaymentManagementItemDto;
}) {

    if (
        payment.status ===
        PaymentMilestoneStatus.Cancelled
    ) {
        return (
            <Chip
                label="Cancelled"
                size="small"
                variant="outlined"
            />
        );
    }

    if (
        payment.status ===
        PaymentMilestoneStatus.Paid
    ) {
        return (
            <Chip
                label="Paid"
                size="small"
                color="success"
            />
        );
    }

    if (payment.isOverdue) {
        return (
            <Chip
                label="Overdue"
                size="small"
                color="error"
            />
        );
    }

    if (payment.isDueToday) {
        return (
            <Chip
                label="Due today"
                size="small"
                color="warning"
                icon={<Today />}
            />
        );
    }

    if (
        payment.status ===
        PaymentMilestoneStatus.PartPaid
    ) {
        return (
            <Chip
                label="Part paid"
                size="small"
                color="warning"
                variant="outlined"
            />
        );
    }

    if (
        payment.daysUntilDue != null &&
        payment.daysUntilDue >= 0 &&
        payment.daysUntilDue <= 7
    ) {
        return (
            <Chip
                label="Due soon"
                size="small"
                color="warning"
                variant="outlined"
            />
        );
    }

    return (
        <Chip
            label="Planned"
            size="small"
            variant="outlined"
        />
    );
}

// -----------------------------------------------------------------------------
// Other small components
// -----------------------------------------------------------------------------

function QuickFilterChip({
    label,
    selected,
    onClick,
}: {
    label: string;
    selected: boolean;
    onClick: () => void;
}) {

    return (
        <Chip
            label={label}
            clickable
            color={
                selected
                    ? "primary"
                    : "default"
            }
            variant={
                selected
                    ? "filled"
                    : "outlined"
            }
            onClick={onClick}
        />
    );
}

function MobileValue({
    label,
    value,
    strong = false,
}: {
    label: string;
    value: string;
    strong?: boolean;
}) {

    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
            >
                {label}
            </Typography>

            <Typography
                variant="body2"
                fontWeight={
                    strong ? 600 : 400
                }
            >
                {value}
            </Typography>
        </Box>
    );
}

function EmptyChart() {

    return (
        <Box
            sx={{
                height: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Typography
                variant="body2"
                color="text.secondary"
            >
                No upcoming payment commitments.
            </Typography>
        </Box>
    );
}

function compactCurrency(
    amount: number,
    currency: string,
): string {

    try {
        return new Intl.NumberFormat(
            "en-US",
            {
                style: "currency",
                currency,
                notation: "compact",
                maximumFractionDigits: 1,
            },
        ).format(amount);
    }
    catch {
        return amount.toLocaleString();
    }
}